import { useState, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Database } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Fix 4: per-source, per-connected-account ingestion status.
 *
 * Answers why connected accounts show 0 transactions: never synced, sync
 * erroring, synced-but-empty, or no sync metadata recorded. Zero-transaction
 * accounts are listed first. Read-only.
 */
const CLASS_BADGE = {
  sync_erroring: { label: 'sync erroring', variant: 'destructive' },
  never_synced: { label: 'never synced', variant: 'destructive' },
  no_metadata: { label: 'no metadata', variant: 'outline' },
  synced_but_empty: { label: 'synced but empty', variant: 'secondary' },
  has_transactions: { label: 'has transactions', variant: 'default' },
};

const VERDICT_BADGE = {
  'IMPLEMENTED-AND-WORKING': { variant: 'default' },
  'IMPLEMENTED-BUT-FAILING': { variant: 'destructive' },
  'BLOCKED-ON-CREDENTIALS': { variant: 'destructive' },
  'NOT-IMPLEMENTED': { variant: 'outline' },
};

function VerdictBlock({ verdicts }) {
  if (!verdicts || Object.keys(verdicts).length === 0) return null;
  return (
    <div className="space-y-2 border rounded-md p-3 bg-muted/20" data-testid="ingestion-verdict-block">
      <div className="text-xs font-medium text-muted-foreground">Per-source verdict — read this first</div>
      {Object.entries(verdicts).map(([source, v]) => {
        const cfg = VERDICT_BADGE[v.verdict] || { variant: 'outline' };
        return (
          <div key={source} className="text-xs flex flex-wrap items-start gap-2" data-testid={`verdict-${source}`}>
            <span className="font-mono font-medium min-w-[110px]">{source}</span>
            <Badge variant={cfg.variant} className="text-[10px]">{v.verdict}</Badge>
            <span className="text-muted-foreground">{v.reason}</span>
            {v.operator_action && (
              <span className="text-amber-600">→ {v.operator_action}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LastRequestDetail({ req }) {
  if (!req) {
    return <span className="text-muted-foreground">no transaction request recorded yet</span>;
  }
  const win = req.window_from || req.window_to
    ? `${req.window_from || '(none)'} → ${req.window_to || '(open)'}`
    : 'no date window (newest-first paging)';
  return (
    <div data-testid="last-request-detail" className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px]">
      <span>provider id: {req.provider_account_id || '(org-wide, no account filter)'}</span>
      <span>window: {win}</span>
      <span>http: {req.http_status != null ? req.http_status : '-'}</span>
      <span>items: {req.items_returned != null ? req.items_returned : '-'}</span>
      <span>pages: {req.pages != null ? req.pages : '-'}</span>
      {req.timestamp && <span>at: {String(req.timestamp).slice(0, 19)}</span>}
      {req.note && <span className="w-full text-muted-foreground">{req.note}</span>}
    </div>
  );
}

export function IngestionStatusPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const run = async () => {
    setLoading(true);
    setData(null);
    try {
      const res = await api.get('/admin/diagnostics/ingestion-status', { timeout: 120000 });
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-4 w-4" />
          Ingestion Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Per source and per connected account: last sync, last error, transaction
          count, newest transaction date, and a classification for zero-transaction
          accounts. Read-only diagnostic; the ingestion fixes it verifies (backfill,
          per-account cursor, dedupe) live in the sync code, not on this card.
        </p>

        <Button size="sm" onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
        </Button>

        {data && (
          <div className="space-y-3 text-sm">
            <VerdictBlock verdicts={data.verdicts} />
            <div className="flex gap-4 text-xs font-mono">
              <span>accounts: {data.summary.total_accounts}</span>
              <span className={data.summary.zero_transaction_accounts > 0 ? 'text-amber-600' : ''}>
                zero-tx: {data.summary.zero_transaction_accounts}
              </span>
              {data.summary.duplicates_merged != null && (
                <span className={data.summary.duplicates_merged > 0 ? 'text-blue-600' : ''}>
                  duplicates-merged: {data.summary.duplicates_merged}
                </span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(data.summary.by_classification).map(([c, n]) => {
                const cfg = CLASS_BADGE[c] || { label: c, variant: 'outline' };
                return <Badge key={c} variant={cfg.variant} className="text-[10px]">{cfg.label}: {n}</Badge>;
              })}
            </div>

            <div className="overflow-x-auto">
              <table className="text-xs w-full border-collapse">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1 pr-2">account</th>
                    <th className="py-1 pr-2">source</th>
                    <th className="py-1 pr-2">entity</th>
                    <th className="py-1 pr-2 text-right">tx</th>
                    <th className="py-1 pr-2">newest</th>
                    <th className="py-1 pr-2">last sync</th>
                    <th className="py-1 pr-2">last error</th>
                    <th className="py-1 pr-2">class</th>
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((r) => {
                    const cfg = CLASS_BADGE[r.classification] || { label: r.classification, variant: 'outline' };
                    const lastSync = r.account_last_synced || r.source_last_successful || r.source_last_attempt;
                    const isOpen = !!expanded[r.id];
                    return (
                      <Fragment key={r.id}>
                      <tr
                        onClick={() => toggle(r.id)}
                        className={`border-b border-border/30 cursor-pointer hover:bg-muted/40 ${r.transaction_count === 0 ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}`}
                      >
                        <td className="py-1 pr-2 truncate max-w-[160px]" title={`${r.bank_name} ${r.account_name}`}>
                          <span className="text-muted-foreground mr-1">{isOpen ? '▾' : '▸'}</span>
                          {r.bank_name} <span className="text-muted-foreground">{r.account_name}</span>
                        </td>
                        <td className="py-1 pr-2 font-mono">{r.source}</td>
                        <td className="py-1 pr-2">{r.entity_name || r.entity_id}</td>
                        <td className="py-1 pr-2 font-mono text-right">{r.transaction_count.toLocaleString()}</td>
                        <td className="py-1 pr-2 font-mono">{(r.newest_transaction_date || '').slice(0, 10) || '-'}</td>
                        <td className="py-1 pr-2 font-mono">
                          {lastSync ? String(lastSync).slice(0, 10) : (
                            <span className="text-muted-foreground">{r.source_metadata_note || 'none'}</span>
                          )}
                        </td>
                        <td className="py-1 pr-2 text-red-600 truncate max-w-[140px]" title={r.source_last_error || ''}>
                          {r.source_last_error || ''}
                        </td>
                        <td className="py-1 pr-2">
                          <Badge variant={cfg.variant} className="text-[10px]">{cfg.label}</Badge>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-border/30 bg-muted/20">
                          <td colSpan={8} className="py-2 px-3">
                            <div className="text-[11px] font-medium mb-1 text-muted-foreground">last transaction request</div>
                            <LastRequestDetail req={r.last_request} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
